import { produce } from "immer";
import React, { useMemo } from "react";

import { Text } from "@examspark/common-ui";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";
import { ObjectType } from "@examspark/types";

import { getLabelForObjectType } from "../../../utils";
import { Filter, FilterData } from "../Browse";
import {
  getTermAggregationBucketValue,
  useSearch,
  useSearchFn,
} from "../useSearch";

import FilterCheckboxList from "./FilterCheckboxList";

const typeDescriptors: Array<{
  value: ObjectType;
  sk?: string;
}> = [
  { value: "COURSE", sk: "course#v0" },
  { value: "UNIT", sk: "unit#v0" },
  { value: "COURSE_PLAN", sk: "course-plan#v0" },
  { value: "COURSE_VERIFICATION", sk: "course-verification#v0" },
  { value: "CLARIFICATION" },
  { value: "VERTICAL_ALIGNMENT" },
  { value: "CONCEPTUAL_ALIGNMENT" },
  { value: "VOCABULARY", sk: "vocabulary" },
  { value: "PERFORMANCE_TASK" },
  { value: "RUBRIC" },
];

const FilterType = (props: {
  onChange: (values: ObjectType[]) => void;
  useSearchFn?: useSearchFn;
  filters: FilterData;
}) => {
  const filter = useMemo(() => {
    const { type, ...filter } = props.filters;
    return filter;
  }, [props.filters]);

  const debouncedFilter = useDebounce(filter, 500);

  const searchFn = props.useSearchFn ?? useSearch;

  const { data } = searchFn({
    queryKey: ["aggs", "type", debouncedFilter],
    searchLabel: "SearchAggsType",
    limit: 0,
    filter: debouncedFilter,
  });

  return (
    <Filter>
      <Text.H2>Type</Text.H2>
      <FilterCheckboxList>
        {typeDescriptors.map((type) => {
          const count =
            data?.aggregations &&
            getTermAggregationBucketValue(
              data?.aggregations,
              type.sk ? "sk" : "type",
              type.sk ?? type.value,
            );

          return (
            <FilterCheckboxList.Item
              key={type.value}
              count={count ?? 0}
              onClick={() => {
                if (!props.filters.type) return;
                props.onChange(
                  produce(props.filters.type, (draft) => {
                    if (draft.includes(type.value)) {
                      draft.splice(draft.indexOf(type.value), 1);
                    } else {
                      draft.push(type.value);
                    }
                  }),
                );
              }}
              checked={props.filters.type?.includes(type.value)}
            >
              {getLabelForObjectType(type.value)}
            </FilterCheckboxList.Item>
          );
        })}
      </FilterCheckboxList>
    </Filter>
  );
};

export default FilterType;
